import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, useToast } from '../../components/common';
import api from '../../services/api';
import './DraftBar.css';

/**
 * What is staged, across every tab of this app — and the moment you make it real.
 *
 * Editing used to apply immediately, so four fields meant four versions and
 * there was no moment where you decided the app should now be different. Edits
 * are collected here instead, and committing them is that moment.
 *
 * The draft is the same one `paradigm changes` reads — it lives on the app, not
 * in this tab — so a change staged in a terminal shows up here and vice versa.
 *
 * THE BAR NO LONGER COMMITS. It used to carry a Commit button beside Review,
 * which meant the most consequential action in the product — altering the terms
 * 128 people agreed to — was reachable without reading what it would alter. The
 * bar announces; the drawer decides.
 *
 * THIS FILE FORMATS NO MONEY. A staged price is rendered by the server's one
 * renderer (`POST /third-party/pricing/preview`), the same one the consent card
 * and store badge use. A local formatter here would be a second renderer, and
 * then this drawer could promise a price the consent screen contradicts.
 */
const LABELS = {
  yaml: 'access request',
  crux: 'CRUX',
  website_url: 'website',
  repository_url: 'repository',
  redirect_uri: 'redirect URI',
  redirect_uris: 'redirect URIs',
  deployment_mode: 'deployment',
  pricing: 'price',
  app_store_visibility: 'store visibility',
  webhook_url: 'webhook URL',
  is_active: 'status',
};

function short(v) {
  if (v === null || v === undefined || v === '') return 'not set';
  const t = typeof v === 'string' ? v : JSON.stringify(v);
  const one = t.replace(/\s+/g, ' ').trim();
  return one.length > 90 ? one.slice(0, 89) + '…' : one;
}

/**
 * The two ways out of a widening change, in the words of what happens to people.
 *
 * This was a `window.confirm` whose first line read "OK — cancel existing
 * authorizations", so the safe choice was the one you got by pressing Cancel and
 * the destructive one was the default-looking button. Worse, the question gave
 * no hint that "continue" is the ordinary answer: existing grants already carry
 * a frozen copy of what each person agreed to, so continuing is literally doing
 * nothing to them. That is the recommended path and it now says so.
 */
const FORK = [
  {
    action: 'continue',
    title: 'Keep them on what they granted',
    body: 'Nothing changes for anyone who already authorized — their agreed terms are '
        + 'already frozen on each grant. Only new users meet the new terms. The added '
        + 'access fails for existing users until each one chooses to re-authorize.',
    recommended: true,
  },
  {
    action: 'cancel',
    title: 'Ask everyone to authorize again',
    body: 'Existing authorizations are revoked. Access stops for everyone until each '
        + 'person approves the new terms. Choose this when the old grant is genuinely '
        + 'wrong, not merely out of date.',
    recommended: false,
  },
];

function DraftBar({ appId, draft, specVersion, onChanged }) {
  const toast = useToast();
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  // null while the change is not a widening one; otherwise why it widened, so the
  // fork can say whether it is about permissions or about money.
  const [fork, setFork] = useState(null);
  const [epAction, setEpAction] = useState('continue');
  // Rendered price strings, keyed 'from' | 'to'. Server-rendered, never built here.
  const [prices, setPrices] = useState(null);

  const changes = draft?.changes || [];
  const priceChange = changes.find(c => c.field === 'pricing');

  /* A staged price is an object on the wire. Showing the raw JSON would be both
     unreadable and a second opinion about what the price says, so both sides are
     sent through the one renderer instead. Failure degrades to the plain field
     summary rather than blocking the review. */
  useEffect(() => {
    if (!reviewing || !priceChange) { setPrices(null); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const [from, to] = await Promise.all([
          api.previewPricing(priceChange.from || { model: 'free' }),
          api.previewPricing(priceChange.to || { model: 'free' }),
        ]);
        if (!cancelled) {
          setPrices({
            from: from?.pricing_display?.headline || null,
            to: to?.pricing_display?.headline || null,
          });
        }
      } catch {
        if (!cancelled) setPrices(null);
      }
    })();
    return () => { cancelled = true; };
  }, [reviewing, priceChange]);

  const commit = useCallback(async (action) => {
    setBusy(true);
    try {
      const res = await api.request(`/third-party/${appId}/draft/commit`, {
        method: 'POST',
        body: JSON.stringify(action ? { ep_action: action } : {}),
      });
      toast.success(`Committed. Now v${res.spec_version}.`);
      setReviewing(false);
      setFork(null);
      await onChanged();
    } catch (err) {
      // Widening what the app asks for needs a decision about the people who
      // already granted the narrower version. The draft is kept either way — the
      // backend puts it back before answering — so nothing is lost by stopping
      // here and asking properly.
      if (err.code === 'EP_ACTION_REQUIRED' || /ep_action/i.test(err.message || '')) {
        setFork(/price/i.test(err.message || '') ? 'price' : 'access');
        setReviewing(true);
        setBusy(false);
        return;
      }
      toast.error(err.message || 'Could not commit.');
    }
    setBusy(false);
  }, [appId, onChanged, toast]);

  const discard = useCallback(async () => {
    const n = draft?.count || changes.length;
    if (!window.confirm(`Discard ${n} staged change${n === 1 ? '' : 's'}? The app is unaffected — they were never applied.`)) return;
    setBusy(true);
    try {
      await api.request(`/third-party/${appId}/draft`, { method: 'DELETE' });
      toast.success('Discarded.');
      setReviewing(false);
      setFork(null);
      await onChanged();
    } catch (err) {
      toast.error(err.message || 'Could not discard.');
    }
    setBusy(false);
  }, [appId, changes.length, draft, onChanged, toast]);

  if (!draft?.has_changes) return null;
  const count = draft.count || changes.length;
  const nextVersion = (specVersion || 1) + 1;

  return (
    <>
      <div className="draftbar" role="status">
        <span className="draftbar__count">
          <strong>{count}</strong> staged change{count === 1 ? '' : 's'}
        </span>
        <span className="draftbar__what">
          {changes.map(c => LABELS[c.field] || c.field).join(' · ')}
        </span>
        {/* "Not live yet" said what was not true of them. What matters is what
            people are actually getting, which is the version before this one. */}
        <span className="draftbar__note">
          Everyone is still on v{specVersion}.
          {/* One draft per app, wherever it was edited — so on a shared app the
              pending change may well be somebody else's unfinished work. */}
          {draft.updated_by_name && (
            <> Staged by {draft.updated_by_name}
              {draft.updated_at && <> · {new Date(draft.updated_at).toLocaleString()}</>}.
            </>
          )}
        </span>
        <div className="draftbar__actions">
          {/* One door, and it leads to the changes rather than past them. */}
          <Button size="sm" onClick={() => setReviewing(true)}>
            Review &amp; commit
          </Button>
        </div>
      </div>

      {reviewing && (
        <Modal isOpen onClose={() => setReviewing(false)} title="Review & commit" size="lg">
          <p className="draftbar__lede">
            Your app is serving <strong>v{specVersion}</strong> and keeps doing so until you
            commit. These {count === 1 ? 'changes go' : 'changes all go'} live together, as
            {' '}<strong>v{nextVersion}</strong>.
          </p>

          <ul className="draftdiff">
            {changes.map(c => {
              const isPrice = c.field === 'pricing';
              const from = isPrice && prices?.from ? prices.from : short(c.from);
              const to = isPrice && prices?.to ? prices.to : short(c.to);
              return (
                <li key={c.field}>
                  <div className="draftdiff__field">{LABELS[c.field] || c.field}</div>
                  <div className="draftdiff__from"><span>now</span> {from}</div>
                  <div className="draftdiff__to"><span>→</span> {to}</div>
                </li>
              );
            })}
          </ul>

          {fork && (
            <div className="draftfork">
              <h4 className="draftfork__title">
                {fork === 'price'
                  ? 'This asks more of people who already pay'
                  : `This asks for more than v${specVersion} did`}
              </h4>
              <p className="draftfork__lede">
                People have already agreed to the current terms. You have to say what
                happens to them — ofSelf will not change what somebody agreed to
                without being told, in either direction.
              </p>
              <div className="draftfork__opts">
                {FORK.map(opt => (
                  <button
                    key={opt.action}
                    type="button"
                    className="draftfork__opt"
                    data-active={epAction === opt.action || undefined}
                    aria-pressed={epAction === opt.action}
                    onClick={() => setEpAction(opt.action)}
                  >
                    <span className="draftfork__mark" aria-hidden="true" />
                    <span className="draftfork__text">
                      <span className="draftfork__opt-title">
                        {opt.title}
                        {opt.recommended && <em className="draftfork__rec">usually this</em>}
                      </span>
                      <span className="draftfork__opt-body">{opt.body}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="draftdiff__actions">
            <Button variant="secondary" onClick={discard} disabled={busy}>Discard all</Button>
            <Button variant="secondary" onClick={() => setReviewing(false)} disabled={busy}>
              Keep editing
            </Button>
            <Button onClick={() => commit(fork ? epAction : undefined)} disabled={busy}>
              {busy ? 'Committing…' : `Commit to v${nextVersion}`}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

export default DraftBar;
